import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import Badge from '../../../common/components/badge';
import ConfirmDialog from '../../../common/components/confirm-dialog';
import { IconEdit, IconPlus, IconTrash } from '../../../common/icons';
import { envelopeData } from '../../../core/network/envelope';
import { fmtMoney } from '../../../core/utils/format';
import { submissionKeys } from '../queries/keys';
import { useSubmissionsList, useWithdrawSubmission } from '../contexts/submissions';
import SubmissionForm from '../forms/submission-form';

import '../styles/overview.css';

// TSS vite/03 — "Mis propuestas": donadoras externas propuestas por el cliente
// (staging con curaduría, public-donor-submissions.md / Q-122). Lectura envuelta
// (envelope view "donor_submissions") → envelopeData(). Ciclo de vida:
//   pending  → editar / retirar (Badge warn)
//   verified → ya es tu donadora → enlace a /mis-vacas (Badge good)
//   rejected → se muestra review_note (Badge bad)
// Filtro opcional por verification_state (URL state). Paginación offset.

const PAGE_SIZE = 50;

const STATE_META = {
    pending:  { label: 'En revisión', tone: 'warn' },
    verified: { label: 'Verificada',  tone: 'good' },
    rejected: { label: 'Rechazada',   tone: 'bad' },
};

const FILTERS = [
    { value: '', label: 'Todas' },
    { value: 'pending', label: 'En revisión' },
    { value: 'verified', label: 'Verificadas' },
    { value: 'rejected', label: 'Rechazadas' },
];

function SubmissionCard({ submission, onEdit, onWithdraw }) {
    const meta = STATE_META[submission.verification_state] ?? { label: submission.verification_state, tone: 'muted' };
    const pending = submission.verification_state === 'pending';

    return (
        <article className="propuesta-card">
            <div className="propuesta-card__main">
                <div className="propuesta-card__head">
                    <span className="propuesta-card__name">
                        {submission.name || (submission.number ? `Donadora ${submission.number}` : `Propuesta #${submission.id}`)}
                    </span>
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                </div>
                <div className="propuesta-card__meta">
                    {submission.number && <span className="propuesta-card__number">Nº {submission.number}</span>}
                    {submission.race?.name && <span>{submission.race.name}</span>}
                    {submission.origin_farm?.name && <span>{submission.origin_farm.name}</span>}
                    {submission.purchase_value != null && (
                        <span className="propuesta-card__value">{fmtMoney(submission.purchase_value)}</span>
                    )}
                </div>

                {submission.verification_state === 'rejected' && submission.review_note && (
                    <p className="propuesta-card__note">
                        <strong>Motivo del rechazo:</strong> {submission.review_note}
                    </p>
                )}
                {submission.verification_state === 'verified' && (
                    <p className="propuesta-card__verified">
                        Ya es tu donadora — <Link to="/mis-vacas">ver en Mis vacas</Link>
                    </p>
                )}
            </div>

            {pending && (
                <div className="propuesta-card__actions">
                    <button
                        type="button"
                        className="propuesta-card__action"
                        onClick={() => onEdit(submission)}
                        aria-label={`Editar propuesta ${submission.number || submission.id}`}
                    >
                        <IconEdit />
                    </button>
                    <button
                        type="button"
                        className="propuesta-card__action propuesta-card__action--danger"
                        onClick={() => onWithdraw(submission)}
                        aria-label={`Retirar propuesta ${submission.number || submission.id}`}
                    >
                        <IconTrash />
                    </button>
                </div>
            )}
        </article>
    );
}

export default function DonadorasExternasOverview() {
    const [params, setParams] = useSearchParams();
    const qc = useQueryClient();

    const offset = Math.max(0, Number(params.get('offset')) || 0);
    const stateFilter = params.get('estado') || '';

    const { data, isLoading, isFetching, isError, error } = useSubmissionsList({
        limit: PAGE_SIZE,
        offset,
        verification_state: stateFilter || undefined,
    });

    const payload = envelopeData(data);
    const items = payload.items ?? [];
    const pagination = payload.pagination ?? {};
    const total = pagination.total ?? null;

    const hasPrev = offset > 0;
    const hasNext = total != null ? offset + items.length < total : items.length === PAGE_SIZE;

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [withdrawing, setWithdrawing] = useState(null);

    const withdraw = useWithdrawSubmission();

    const setFilter = (value) => {
        const next = new URLSearchParams(params);
        if (value) next.set('estado', value); else next.delete('estado');
        next.delete('offset'); // cambiar filtro resetea la página
        setParams(next, { replace: true });
    };

    const goTo = (nextOffset) => {
        const next = new URLSearchParams(params);
        if (nextOffset > 0) next.set('offset', String(nextOffset));
        else next.delete('offset');
        setParams(next, { replace: true });
    };

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (submission) => { setEditing(submission); setFormOpen(true); };
    const closeForm = () => { setFormOpen(false); setEditing(null); };

    const confirmWithdraw = () => {
        if (!withdrawing) return;
        withdraw.mutate(withdrawing.id, {
            onSuccess: () => {
                toast.success('Propuesta retirada');
                setWithdrawing(null);
            },
            onError: (err) => {
                if (err?.status === 409) {
                    // Defensivo: ya fue revisada mientras estaba en pantalla.
                    toast.error('Esta propuesta ya fue revisada');
                    qc.invalidateQueries({ queryKey: submissionKeys.all });
                    setWithdrawing(null);
                } else if (err?.status === 404) {
                    toast.error('Propuesta no encontrada');
                    qc.invalidateQueries({ queryKey: submissionKeys.all });
                    setWithdrawing(null);
                } else if (err?.status !== 403) {
                    toast.error(err?.message || 'No se pudo retirar la propuesta');
                }
            },
        });
    };

    if (isLoading) {
        return <div className="propuestas__loading">Cargando tus propuestas...</div>;
    }

    if (isError) {
        return (
            <div className="propuestas__error">
                {error?.message || 'No se pudieron cargar tus propuestas'}
            </div>
        );
    }

    return (
        <section className="propuestas">
            <header className="propuestas__header">
                <div className="propuestas__heading">
                    <h2 className="propuestas__title">Mis propuestas de donadoras</h2>
                    {total != null && (
                        <span className="propuestas__count">
                            {total} propuesta{total === 1 ? '' : 's'}
                        </span>
                    )}
                </div>
                <button type="button" className="propuestas__new" onClick={openCreate}>
                    <IconPlus />
                    <span>Proponer donadora</span>
                </button>
            </header>

            <div className="propuestas__filters" role="group" aria-label="Filtrar por estado">
                {FILTERS.map((f) => (
                    <button
                        key={f.value || 'all'}
                        type="button"
                        className={`propuestas__filter ${stateFilter === f.value ? 'propuestas__filter--active' : ''}`}
                        onClick={() => setFilter(f.value)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {!items.length ? (
                <div className="propuestas__empty">
                    <p className="propuestas__empty-text">
                        {stateFilter
                            ? 'No tienes propuestas en este estado.'
                            : 'Aún no has propuesto donadoras. Propón la primera y el equipo la revisará.'}
                    </p>
                    {!stateFilter && (
                        <button type="button" className="propuestas__empty-cta" onClick={openCreate}>
                            <IconPlus />
                            <span>Proponer donadora</span>
                        </button>
                    )}
                </div>
            ) : (
                <div className={`propuestas__list ${isFetching ? 'propuestas__list--refreshing' : ''}`}>
                    {items.map((submission) => (
                        <SubmissionCard
                            key={submission.id}
                            submission={submission}
                            onEdit={openEdit}
                            onWithdraw={setWithdrawing}
                        />
                    ))}
                </div>
            )}

            {(hasPrev || hasNext) && (
                <nav className="propuestas__pager">
                    <button
                        className="propuestas__pager-btn"
                        onClick={() => goTo(offset - PAGE_SIZE)}
                        disabled={!hasPrev || isFetching}
                    >
                        ← Anteriores
                    </button>
                    <span className="propuestas__pager-info">
                        {offset + 1}–{offset + items.length}
                    </span>
                    <button
                        className="propuestas__pager-btn"
                        onClick={() => goTo(offset + PAGE_SIZE)}
                        disabled={!hasNext || isFetching}
                    >
                        Siguientes →
                    </button>
                </nav>
            )}

            {formOpen && (
                <SubmissionForm submission={editing} onClose={closeForm} onSaved={closeForm} />
            )}

            <ConfirmDialog
                open={!!withdrawing}
                title="Retirar propuesta"
                message={
                    withdrawing
                        ? `¿Seguro que quieres retirar la propuesta "${withdrawing.name || withdrawing.number || `#${withdrawing.id}`}"? Dejará de estar en revisión.`
                        : ''
                }
                confirmLabel="Retirar"
                danger
                busy={withdraw.isPending}
                onConfirm={confirmWithdraw}
                onClose={() => setWithdrawing(null)}
            />
        </section>
    );
}
